import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, Smartphone, Check, RefreshCw, Phone } from 'lucide-react';
import PhoneIllustration from './Buyback/PhoneIllustration';
import { PRICES } from './Buyback/prices';

// Allorep proposes BM_price - 40€ on every combination.
const OFF = 40;

const SERIES = [
  {id:'s17',l:'iPhone 17',n:true},
  {id:'s16',l:'iPhone 16',n:true},
  {id:'s15',l:'iPhone 15'},
  {id:'s14',l:'iPhone 14'},
  {id:'s13',l:'iPhone 13'},
  {id:'s12',l:'iPhone 12'},
  {id:'s11',l:'iPhone 11'},
  {id:'sX', l:'iPhone X / XS / XR'},
];

const MODELS = {
  s17:[{id:'17pm',l:'iPhone 17 Pro Max'},{id:'17p',l:'iPhone 17 Pro'},{id:'17air',l:'iPhone 17 Air'},{id:'17',l:'iPhone 17'}],
  s16:[{id:'16pm',l:'iPhone 16 Pro Max'},{id:'16p',l:'iPhone 16 Pro'},{id:'16plus',l:'iPhone 16 Plus'},{id:'16',l:'iPhone 16'}],
  s15:[{id:'15pm',l:'iPhone 15 Pro Max'},{id:'15p',l:'iPhone 15 Pro'},{id:'15plus',l:'iPhone 15 Plus'},{id:'15',l:'iPhone 15'}],
  s14:[{id:'14pm',l:'iPhone 14 Pro Max'},{id:'14p',l:'iPhone 14 Pro'},{id:'14plus',l:'iPhone 14 Plus'},{id:'14',l:'iPhone 14'}],
  s13:[{id:'13pm',l:'iPhone 13 Pro Max'},{id:'13p',l:'iPhone 13 Pro'},{id:'13',l:'iPhone 13'},{id:'13mini',l:'iPhone 13 mini'}],
  s12:[{id:'12pm',l:'iPhone 12 Pro Max'},{id:'12p',l:'iPhone 12 Pro'},{id:'12',l:'iPhone 12'},{id:'12mini',l:'iPhone 12 mini'}],
  s11:[{id:'11pm',l:'iPhone 11 Pro Max'},{id:'11p',l:'iPhone 11 Pro'},{id:'11',l:'iPhone 11'}],
  sX: [{id:'xsmax',l:'iPhone XS Max'},{id:'xs',l:'iPhone XS'},{id:'xr',l:'iPhone XR'},{id:'x',l:'iPhone X'}],
};

const STORAGES = {
  '17pm':['256 Go','512 Go','1 To'],'17p':['256 Go','512 Go','1 To'],
  '17air':['128 Go','256 Go','512 Go'],'17':['128 Go','256 Go','512 Go'],
  '16pm':['256 Go','512 Go','1 To'],'16p':['128 Go','256 Go','512 Go','1 To'],
  '16plus':['128 Go','256 Go','512 Go'],'16':['128 Go','256 Go','512 Go'],
  '15pm':['256 Go','512 Go','1 To'],'15p':['128 Go','256 Go','512 Go','1 To'],
  '15plus':['128 Go','256 Go','512 Go'],'15':['128 Go','256 Go','512 Go'],
  '14pm':['128 Go','256 Go','512 Go','1 To'],'14p':['128 Go','256 Go','512 Go','1 To'],
  '14plus':['128 Go','256 Go','512 Go'],'14':['128 Go','256 Go','512 Go'],
  '13pm':['128 Go','256 Go','512 Go','1 To'],'13p':['128 Go','256 Go','512 Go','1 To'],
  '13':['128 Go','256 Go','512 Go'],'13mini':['128 Go','256 Go','512 Go'],
  '12pm':['128 Go','256 Go','512 Go'],'12p':['128 Go','256 Go','512 Go'],
  '12':['64 Go','128 Go','256 Go'],'12mini':['64 Go','128 Go','256 Go'],
  '11pm':['64 Go','256 Go','512 Go'],'11p':['64 Go','256 Go','512 Go'],'11':['64 Go','128 Go','256 Go'],
  'xsmax':['64 Go','256 Go','512 Go'],'xs':['64 Go','256 Go','512 Go'],
  'xr':['64 Go','128 Go','256 Go'],'x':['64 Go','256 Go'],
};

/*
 * PRICES is imported from ./Buyback/prices.js — it contains the exact
 * Backmarket buyback price for every (model, storage, screen_state,
 * body_state, functional_state) combination, scraped from the BM API.
 *
 * Key format inside each storage object: `s<screen>b<body>f<func>`
 *   screen, body ∈ {1,2,3,4}  (1 = Parfait, 2 = Très bon, 3 = Correct, 4 = Cassé)
 *   func ∈ {1,2}              (1 = Fonctionnel Oui, 2 = Non)
 *
 * Final Allorep price = max(0, BM_price - OFF).
 * Entries set to 0 mean Backmarket does not currently buy that config.
 */

/* 4-level screen condition - matches Backmarket exactly */
const ECRAN = [
  { id:'e1', l:'Parfait état',  d:'Aucune rayure visible, dalle impeccable',                      c:'#10B981', face:'front', state:'perfect' },
  { id:'e2', l:'Très bon état', d:'Micro-rayures à peine visibles, aucun impact sur l\'usage',    c:'#34D399', face:'front', state:'micro'   },
  { id:'e3', l:'État correct',  d:'Rayures visibles à l\'œil mais écran fonctionnel',             c:'#F59E0B', face:'front', state:'visible' },
  { id:'e4', l:'Cassé',         d:'Fissures, éclats, pixels morts ou problème d\'affichage',      c:'#DC2626', face:'front', state:'broken'  },
];

/* 4-level back/frame condition - matches Backmarket exactly */
const DOS = [
  { id:'d1', l:'Parfait état',  d:'Aucune trace sur le dos ni le cadre',                          c:'#10B981', face:'back', state:'perfect' },
  { id:'d2', l:'Très bon état', d:'Légères marques d\'usage à peine visibles',                    c:'#34D399', face:'back', state:'micro'   },
  { id:'d3', l:'État correct',  d:'Rayures visibles, petites bosses sur le cadre',                c:'#F59E0B', face:'back', state:'visible' },
  { id:'d4', l:'Cassé',         d:'Vitre arrière fissurée ou chocs importants',                   c:'#DC2626', face:'back', state:'broken'  },
];

/* 2-level functional state - matches Backmarket Oui/Non */
const FONCT = [
  { id:'f1', l:'Oui',  d:'L\'appareil s\'allume, se charge et toutes les fonctions sont opérationnelles (Face/Touch ID, caméra, haut-parleurs, micros, boutons, Wi-Fi, Bluetooth).', c:'#10B981' },
  { id:'f2', l:'Non',  d:'Au moins une fonction est défectueuse (batterie HS, Face ID, caméra, charge, son, etc.).',                                                                  c:'#DC2626' },
];

const ML = {};
Object.values(MODELS).flat().forEach(m => { ML[m.id] = m.l; });

const TOTAL_STEPS = 6;

const BuybackConfigurator = () => {
  const [step, setStep] = useState(1);
  const [selection, setSelection] = useState({
    series: null, model: null, storage: null,
    ecran: null, dos: null, fonct: null,
  });
  const containerRef = useRef(null);
  const prevStepRef = useRef(step);

  // Auto-scroll wizard into view when step actually changes (mobile UX fix).
  useEffect(() => {
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    const t = setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(t);
  }, [step]);

  // Direct Backmarket lookup. The key combines screen state, body state and
  // functional state exactly the way BM's API encodes them — every one of the
  // 32 combinations per (model, storage) is stored in PRICES.
  // Allorep offer = max(0, BM_price - OFF).
  const calcPrice = () => {
    if (!selection.model || !selection.storage) return 0;
    const ei = ECRAN.findIndex(x => x.id === selection.ecran);
    const di = DOS.findIndex(x => x.id === selection.dos);
    const fi = FONCT.findIndex(x => x.id === selection.fonct);
    if (ei < 0 || di < 0 || fi < 0) return 0;
    const key = `s${ei + 1}b${di + 1}f${fi + 1}`;
    const bm = PRICES[selection.model]?.[selection.storage]?.[key] ?? 0;
    return Math.max(0, bm - OFF);
  };

  const NEXT = { series: 2, model: 3, storage: 4, ecran: 5, dos: 6, fonct: 7 };

  const pick = (key, val) => {
    setSelection(prev => ({ ...prev, [key]: val }));
    setStep(NEXT[key]);
  };

  const goBack = (toStep) => setStep(toStep);

  const restart = () => {
    setStep(1);
    setSelection({
      series: null, model: null, storage: null,
      ecran: null, dos: null, fonct: null,
    });
  };

  const generateWhatsAppMessage = () => {
    const price = calcPrice();
    const modelName = ML[selection.model] || '';
    const el = ECRAN.find(x => x.id === selection.ecran)?.l || '';
    const dl = DOS.find(x => x.id === selection.dos)?.l || '';
    const fl = FONCT.find(x => x.id === selection.fonct)?.l || '';
    return encodeURIComponent(
      `Bonjour AlloRéparations,\n\nJe souhaite vendre mon iPhone :\n` +
      `- Modèle : ${modelName}\n` +
      `- Stockage : ${selection.storage}\n` +
      `- Écran : ${el}\n` +
      `- Coque / cadre : ${dl}\n` +
      `- Fonctionnel : ${fl}\n` +
      `- Prix estimé : ${price}€\n\n` +
      `Merci de me confirmer l'offre.`
    );
  };

  const StepBar = () => (
    <div className="mb-6">
      <div className="flex gap-1 mb-2">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(i => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
              i < step ? 'bg-emerald-300' : i === step ? 'bg-emerald-500' : 'bg-slate-200'
            }`}
          />
        ))}
      </div>
      <p className="text-xs text-slate-500">Étape {Math.min(step, TOTAL_STEPS)} sur {TOTAL_STEPS}</p>
    </div>
  );

  const BackButton = ({ toStep }) => (
    <button
      onClick={() => goBack(toStep)}
      className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4 py-2 px-1 -ml-1 min-h-[24px]"
      data-testid="buyback-back-btn"
    >
      <ChevronLeft size={16} />
      Retour
    </button>
  );

  /* Card for text-only criteria (fonctionnement) */
  const CriteriaButton = ({ item, keyName }) => (
    <button
      onClick={() => pick(keyName, item.id)}
      className="w-full flex items-start gap-4 p-4 bg-white border-2 border-slate-200 rounded-xl text-left hover:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200"
      data-testid={`buyback-${keyName}-${item.id}`}
    >
      <div
        className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
        style={{ backgroundColor: item.c }}
      />
      <div>
        <div className="font-semibold text-slate-900 text-sm">{item.l}</div>
        <div className="text-xs text-slate-500 mt-1 leading-relaxed">{item.d}</div>
      </div>
    </button>
  );

  /* Card with illustrated phone condition */
  const IllustratedCard = ({ item, keyName, illustrationSize = 96 }) => {
    const selected = selection[keyName] === item.id;
    return (
      <button
        onClick={() => pick(keyName, item.id)}
        className={`group relative p-5 rounded-2xl border-2 text-left transition-all duration-300 ${
          selected
            ? 'border-emerald-500 bg-emerald-50/60 shadow-lg shadow-emerald-500/15'
            : 'border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 hover:shadow-md'
        }`}
        data-testid={`buyback-${keyName}-${item.id}`}
      >
        <div className="flex items-center gap-4">
          <div className="shrink-0 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-3">
            <PhoneIllustration face={item.face} state={item.state} size={illustrationSize} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: item.c }}
              />
              <span className="font-semibold text-slate-900 text-sm">{item.l}</span>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">{item.d}</p>
          </div>
          {selected && (
            <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check size={14} className="text-white" />
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <div ref={containerRef} className="min-h-[320px]" data-testid="buyback-configurator" style={{ scrollMarginTop: 90 }}>
      {/* Step 1: Series */}
      {step === 1 && (
        <div data-testid="buyback-step-1">
          <StepBar />
          <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Quelle série d'iPhone ?</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {SERIES.map(s => (
              <button
                key={s.id}
                onClick={() => pick('series', s.id)}
                className="relative py-4 px-4 rounded-xl border-2 border-slate-200 bg-white text-center hover:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200"
                data-testid={`buyback-series-${s.id}`}
              >
                <span className="font-medium text-sm text-slate-800">{s.l}</span>
                {s.n && (
                  <span className="block mt-1.5 text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded mx-auto w-fit">
                    Nouveau
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Model */}
      {step === 2 && (
        <div data-testid="buyback-step-2">
          <BackButton toStep={1} />
          <StepBar />
          <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Quel modèle ?</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {(MODELS[selection.series] || []).map(m => (
              <button
                key={m.id}
                onClick={() => pick('model', m.id)}
                className="py-3 px-3 rounded-xl border-2 border-slate-200 bg-white text-center hover:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200"
                data-testid={`buyback-model-${m.id}`}
              >
                <span className="font-medium text-sm text-slate-800">{m.l}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Storage */}
      {step === 3 && (
        <div data-testid="buyback-step-3">
          <BackButton toStep={2} />
          <StepBar />
          <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Quelle capacité de stockage ?</h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-w-lg mx-auto">
            {(STORAGES[selection.model] || []).map(s => (
              <button
                key={s}
                onClick={() => pick('storage', s)}
                className="py-3 px-3 rounded-xl border-2 border-slate-200 bg-white text-center hover:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200"
                data-testid={`buyback-storage-${s.replace(/\s/g, '-')}`}
              >
                <span className="font-medium text-sm text-slate-800">{s}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 4: Screen */}
      {step === 4 && (
        <div data-testid="buyback-step-4">
          <BackButton toStep={3} />
          <StepBar />
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Écran</p>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Dans quel état est l'écran ?</h3>
          <p className="text-sm text-slate-500 mb-6">Sélectionnez l'option qui correspond le plus.</p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {ECRAN.map(item => <IllustratedCard key={item.id} item={item} keyName="ecran" />)}
          </div>
        </div>
      )}

      {/* Step 5: Back + frame */}
      {step === 5 && (
        <div data-testid="buyback-step-5">
          <BackButton toStep={4} />
          <StepBar />
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Coque + cadre</p>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Dans quel état est l'arrière de l'iPhone ?</h3>
          <p className="text-sm text-slate-500 mb-6">Vitre arrière + cadre métallique.</p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {DOS.map(item => <IllustratedCard key={item.id} item={item} keyName="dos" />)}
          </div>
        </div>
      )}

      {/* Step 6: Functional */}
      {step === 6 && (
        <div data-testid="buyback-step-6">
          <BackButton toStep={5} />
          <StepBar />
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">État fonctionnel</p>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Votre appareil est-il fonctionnel ?</h3>
          <p className="text-sm text-slate-500 mb-6">L'appareil s'allume, se charge et toutes ses fonctions marchent.</p>
          <div className="flex flex-col gap-3 max-w-xl mx-auto">
            {FONCT.map(item => <CriteriaButton key={item.id} item={item} keyName="fonct" />)}
          </div>
        </div>
      )}

      {/* Step 7: Result */}
      {step === 7 && (() => {
        const price = calcPrice();
        const modelName = ML[selection.model] || '';
        const el = ECRAN.find(x => x.id === selection.ecran)?.l || '';
        const dl = DOS.find(x => x.id === selection.dos)?.l || '';
        const fl = FONCT.find(x => x.id === selection.fonct)?.l || '';

        return (
          <div className="text-center" data-testid="buyback-step-7">
            <div className="inline-block bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl p-8 mb-8">
              <span className="text-slate-500 text-sm block mb-2">Estimation de rachat</span>
              <div className="text-6xl font-bold bg-gradient-to-r from-emerald-600 to-green-500 bg-clip-text text-transparent">
                {price > 0 ? `${price}€` : 'Non disponible'}
              </div>
              <p className="text-xs text-slate-500 mt-3">Offre valable 7 jours</p>
              <div className="flex justify-center gap-3 mt-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-full text-sm text-emerald-700 font-medium">
                  <Check size={14} />
                  Paiement immédiat en boutique
                </span>
              </div>
            </div>

            <div className="max-w-md mx-auto mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200/70 text-left flex gap-3">
              <div className="shrink-0 w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">
                i
              </div>
              <p className="text-xs text-slate-700 leading-relaxed">
                <span className="font-semibold text-slate-900">Estimation indicative.</span> Le prix final est confirmé après vérification du téléphone en boutique (état réel, IMEI, session iCloud, etc.).
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 text-left max-w-md mx-auto mb-8">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Smartphone size={18} />
                Récapitulatif
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-500">Modèle</span>
                  <span className="text-slate-900 font-medium">{modelName}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-500">Capacité</span>
                  <span className="text-slate-900 font-medium">{selection.storage}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-500">Écran</span>
                  <span className="text-slate-900 font-medium">{el}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-500">Coque + cadre</span>
                  <span className="text-slate-900 font-medium">{dl}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500">Fonctionnel</span>
                  <span className="text-slate-900 font-medium">{fl}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {price > 0 && (
                <a
                  href="tel:+33983820754"
                  className="btn-call text-base"
                  data-testid="buyback-call-cta"
                >
                  <Phone size={18} strokeWidth={2.2} />
                  Appeler pour valider le rachat
                </a>
              )}
              <button
                onClick={restart}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-200 rounded-xl font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-all"
                data-testid="buyback-restart"
              >
                <RefreshCw size={16} />
                Nouvelle estimation
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BuybackConfigurator;
